/**
 * NewClientDialog (BUG 01 + BUG 02).
 *
 * 4-step flow inside one modal:
 *   1) Identity      — name, country, contact, contracted suites
 *   2) Industry      — pick from catalog; "other" reveals free-text input
 *   3) Departments   — preview suggested departments for the industry;
 *                      add/remove/rename; each department holds positions
 *                      (with "Other" → free text)
 *   4) Review/Save   — confirms count of departments + positions
 *
 * Fully bilingual EN/ES via i18n + dual-locale labels coming from the
 * /admin/industries/catalog endpoint (label_en / label_es).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, ChevronRight, ChevronLeft, Building2, Briefcase, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';

const SUITE_CHECKBOXES = [
  { code: 'talentmap',        label: 'TalentMap' },
  { code: 'climara',          label: 'CLIMARA' },
  { code: 'talentcompetency', label: 'TalentCompetency' },
  { code: 'talenttrain',      label: 'TalentTrain' },
  { code: 'talentcheck',      label: 'TalentCheck' },
  { code: 'talentstart',      label: 'TalentStart' },
  { code: 'talentheart',      label: 'TalentHeart' },
  { code: 'talentfix',        label: 'TalentFix' },
  { code: 'intelligence',     label: 'TalentIntelligence' },
  { code: 'workforce_records', label: 'Workforce Records' },
];
// NOTE (UAT B2): Global Ethics is intentionally NOT part of this contracted-suite
// catalog — it is provisioned separately via per-company anonymous access codes.

// Iter 31 (R1) — user-limit tier options shown in the picker
export const USER_LIMIT_TIER_OPTIONS = [
  { code: 'up_to_25', label_en: 'Up to 25',     label_es: 'Hasta 25' },
  { code: '26_50',    label_en: '26 to 50',     label_es: 'De 26 a 50' },
  { code: '51_100',   label_en: '51 to 100',    label_es: 'De 51 a 100' },
  { code: '101_150',  label_en: '101 to 150',   label_es: 'De 101 a 150' },
  { code: '151_plus', label_en: '151 or more',  label_es: '151 o más' },
];

export default function NewClientDialog({ open, onOpenChange, onCreated }) {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || '').toLowerCase().startsWith('es');
  const pick = (en, es) => (isES ? (es || en) : (en || es));

  const [step, setStep] = useState(1);
  const [industries, setIndustries] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [identity, setIdentity] = useState({
    name: '', country: '', contact_email: '', employees_count: '',
    suite_codes: ['talentmap'],
    user_limit_tier: 'up_to_25',
    assigned_consultant_id: '',
  });
  const [industry, setIndustry] = useState({ code: '', other: '' });
  const [departments, setDepartments] = useState([]); // [{name, positions: [string]}]

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setStep(1);
      setIdentity({ name: '', country: '', contact_email: '', employees_count: '', suite_codes: ['talentmap'], user_limit_tier: 'up_to_25', assigned_consultant_id: '' });
      setIndustry({ code: '', other: '' });
      setDepartments([]);
      adminApi.listIndustries().then(setIndustries).catch(() => toast.error(t('toasts.failedToLoadIndustries')));
      adminApi.listConsultants().then(setConsultants).catch(() => setConsultants([]));
    }
  }, [open]);

  // When industry changes, prefetch its default departments → preload as suggestions
  useEffect(() => {
    if (!industry.code || industry.code === 'other') {
      setCatalog(null);
      if (industry.code === 'other') setDepartments([]);
      return;
    }
    adminApi.getIndustry(industry.code).then((data) => {
      setCatalog(data);
      // Preload suggested departments — user can prune/edit
      const seeded = (data.departments || []).map((d) => ({
        name: pick(d.label_en, d.label_es),
        positions: (d.positions || []).map((p) => pick(p.label_en, p.label_es)),
      }));
      setDepartments(seeded);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry.code, isES]);

  const totalPositions = useMemo(
    () => departments.reduce((n, d) => n + (d.positions?.length || 0), 0),
    [departments],
  );

  const canStep1 = identity.name.trim().length > 0 && identity.suite_codes.length > 0;
  const canStep2 = !!industry.code && (industry.code !== 'other' || industry.other.trim().length > 0);
  const canSave  = canStep1 && canStep2;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        name: identity.name.trim(),
        industry_code: industry.code,
        industry_other: industry.code === 'other' ? industry.other.trim() : null,
        country: identity.country.trim() || null,
        contact_email: identity.contact_email.trim() || null,
        employees_count: parseInt(identity.employees_count, 10) || 0,
        suite_codes: identity.suite_codes,
        user_limit_tier: identity.user_limit_tier,
        assigned_consultant_id: identity.assigned_consultant_id || null,
        departments: departments
          .filter((d) => d.name.trim())
          .map((d) => ({ name: d.name.trim(), positions: (d.positions || []).filter((p) => p.trim()) })),
      };
      const res = await adminApi.createClient(body);
      toast.success(t('clients.created', { defaultValue: 'Client created' }), {
        description: res.company.name,
      });
      onCreated?.(res.company);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create client');
    } finally { setSaving(false); }
  };

  // ---- Department editing helpers ----
  const addDepartment = () => setDepartments((p) => [...p, { name: '', positions: [] }]);
  const updateDeptName = (i, name) => setDepartments((p) => p.map((d, idx) => idx === i ? { ...d, name } : d));
  const removeDept = (i) => setDepartments((p) => p.filter((_, idx) => idx !== i));
  const addPosition = (i, val = '') => setDepartments((p) =>
    p.map((d, idx) => idx === i ? { ...d, positions: [...(d.positions || []), val] } : d));
  const updatePosition = (i, j, val) => setDepartments((p) =>
    p.map((d, idx) => idx === i ? { ...d, positions: d.positions.map((x, k) => k === j ? val : x) } : d));
  const removePosition = (i, j) => setDepartments((p) =>
    p.map((d, idx) => idx === i ? { ...d, positions: d.positions.filter((_, k) => k !== j) } : d));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="new-client-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {isES ? 'Nuevo cliente' : 'New client'}
          </DialogTitle>
          <DialogDescription>
            {isES
              ? 'Crear una cuenta de cliente con su catálogo de departamentos y posiciones.'
              : 'Create a client account with its department & position catalog.'}
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground py-2">
          <span className={step === 1 ? 'text-primary' : ''}>{isES ? '1. Identidad' : '1. Identity'}</span>
          <ChevronRight className="h-3 w-3" />
          <span className={step === 2 ? 'text-primary' : ''}>{isES ? '2. Industria' : '2. Industry'}</span>
          <ChevronRight className="h-3 w-3" />
          <span className={step === 3 ? 'text-primary' : ''}>{isES ? '3. Estructura' : '3. Structure'}</span>
          <ChevronRight className="h-3 w-3" />
          <span className={step === 4 ? 'text-primary' : ''}>{isES ? '4. Revisar' : '4. Review'}</span>
        </div>

        {step === 1 && (
          <div className="space-y-4 py-2" data-testid="new-client-step-1">
            <div>
              <Label>{isES ? 'Nombre de la empresa *' : 'Company name *'}</Label>
              <Input
                data-testid="nc-name"
                value={identity.name}
                onChange={(e) => setIdentity({ ...identity, name: e.target.value })}
                placeholder={isES ? 'Acme S.A.' : 'Acme Inc.'}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{isES ? 'País' : 'Country'}</Label>
                <Input data-testid="nc-country" value={identity.country}
                       onChange={(e) => setIdentity({ ...identity, country: e.target.value })}
                       placeholder={t('pages.newClient.countryCodePh')} />
              </div>
              <div>
                <Label>{isES ? 'Email de contacto' : 'Contact email'}</Label>
                <Input data-testid="nc-email" type="email" value={identity.contact_email}
                       onChange={(e) => setIdentity({ ...identity, contact_email: e.target.value })}
                       placeholder={t('pages.newClient.contactEmailPh')} />
              </div>
            </div>
            <div>
              <Label>{isES ? '# de colaboradores (estimado)' : 'Headcount (estimate)'}</Label>
              <Input data-testid="nc-headcount" type="number" value={identity.employees_count}
                     onChange={(e) => setIdentity({ ...identity, employees_count: e.target.value })}
                     placeholder="120" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{isES ? 'Número máximo de usuarios *' : 'Maximum users *'}</Label>
                <Select value={identity.user_limit_tier}
                        onValueChange={(v) => setIdentity({ ...identity, user_limit_tier: v })}>
                  <SelectTrigger data-testid="nc-user-limit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {USER_LIMIT_TIER_OPTIONS.map((o) => (
                      <SelectItem key={o.code} value={o.code}>{pick(o.label_en, o.label_es)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{isES ? 'Consultor asignado' : 'Assigned consultant'}</Label>
                <Select value={identity.assigned_consultant_id || '__none__'}
                        onValueChange={(v) => setIdentity({ ...identity, assigned_consultant_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger data-testid="nc-consultant"><SelectValue placeholder={isES ? 'Sin asignar' : 'Unassigned'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{isES ? 'Sin asignar' : 'Unassigned'}</SelectItem>
                    {consultants.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{isES ? 'Suites contratadas *' : 'Contracted suites *'}</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {SUITE_CHECKBOXES.map((s) => {
                  const checked = identity.suite_codes.includes(s.code);
                  return (
                    <label key={s.code}
                           className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-xs ${checked ? 'border-primary bg-primary/5' : ''}`}>
                      <Checkbox checked={checked}
                                data-testid={`nc-suite-${s.code}`}
                                onCheckedChange={(v) => setIdentity((p) => ({
                                  ...p,
                                  suite_codes: v ? [...p.suite_codes, s.code] : p.suite_codes.filter((x) => x !== s.code),
                                }))} />
                      <span>{s.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-2" data-testid="new-client-step-2">
            <div>
              <Label>{isES ? 'Industria *' : 'Industry *'}</Label>
              <Select value={industry.code} onValueChange={(v) => setIndustry({ code: v, other: '' })}>
                <SelectTrigger data-testid="nc-industry"><SelectValue placeholder={isES ? 'Selecciona una industria' : 'Pick an industry'} /></SelectTrigger>
                <SelectContent>
                  {industries.map((i) => (
                    <SelectItem key={i.code} value={i.code}>
                      {pick(i.label_en, i.label_es)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {industry.code === 'other' && (
              <div>
                <Label>{isES ? 'Nombre de la industria' : 'Industry name'}</Label>
                <Input data-testid="nc-industry-other"
                       value={industry.other}
                       onChange={(e) => setIndustry({ ...industry, other: e.target.value })}
                       placeholder={isES ? 'Escribe la industria…' : 'Type the industry…'} />
              </div>
            )}
            {catalog && (
              <p className="text-xs text-muted-foreground" data-testid="nc-catalog-preview">
                {isES
                  ? `Te preseleccionamos ${catalog.departments.length} departamentos típicos para esta industria. Puedes ajustarlos en el siguiente paso.`
                  : `We pre-selected ${catalog.departments.length} departments typical for this industry. You can adjust them in the next step.`}
              </p>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 py-2" data-testid="new-client-step-3">
            <div className="flex items-center justify-between">
              <div>
                <Label>{isES ? 'Departamentos y posiciones' : 'Departments & positions'}</Label>
                <p className="text-xs text-muted-foreground">
                  {isES ? 'Agrega, edita o elimina. El cliente podrá modificarlo más adelante.' : 'Add, edit or remove. The client can adjust later.'}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={addDepartment} data-testid="nc-add-dept">
                <Plus className="h-3.5 w-3.5 mr-1" /> {isES ? 'Departamento' : 'Department'}
              </Button>
            </div>
            <div className="space-y-3">
              {departments.map((d, i) => (
                <div key={i} className="border rounded p-3 space-y-2" data-testid={`nc-dept-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input value={d.name}
                           onChange={(e) => updateDeptName(i, e.target.value)}
                           placeholder={isES ? 'Nombre del departamento' : 'Department name'}
                           data-testid={`nc-dept-name-${i}`} />
                    <Button size="icon" variant="ghost" onClick={() => removeDept(i)} data-testid={`nc-dept-remove-${i}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="pl-3 border-l-2 border-muted space-y-1.5">
                    {(d.positions || []).map((p, j) => (
                      <div key={j} className="flex items-center gap-1.5">
                        <Briefcase className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <Input value={p}
                               onChange={(e) => updatePosition(i, j, e.target.value)}
                               className="h-8 text-sm"
                               placeholder={isES ? 'Posición' : 'Position'}
                               data-testid={`nc-pos-${i}-${j}`} />
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                                onClick={() => removePosition(i, j)} data-testid={`nc-pos-remove-${i}-${j}`}>
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </div>
                    ))}
                    <Button size="sm" variant="ghost" onClick={() => addPosition(i, '')}
                            data-testid={`nc-add-pos-${i}`} className="text-xs">
                      <Plus className="h-3 w-3 mr-1" /> {isES ? 'Otra posición' : 'Other position'}
                    </Button>
                  </div>
                </div>
              ))}
              {departments.length === 0 && (
                <p className="text-sm text-center italic text-muted-foreground py-4">
                  {isES ? 'Sin departamentos. Agrega el primero.' : 'No departments yet. Add the first one.'}
                </p>
              )}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3 py-2 text-sm" data-testid="new-client-step-4">
            <Row label={isES ? 'Empresa' : 'Company'} value={identity.name} />
            <Row label={isES ? 'Industria' : 'Industry'}
                 value={industry.code === 'other'
                   ? `${isES ? 'Otra' : 'Other'} — ${industry.other}`
                   : (industries.find((i) => i.code === industry.code) || {})[isES ? 'label_es' : 'label_en'] || '—'} />
            <Row label={isES ? 'País' : 'Country'} value={identity.country || '—'} />
            <Row label={isES ? 'Suites' : 'Suites'} value={
              <div className="flex flex-wrap gap-1">
                {identity.suite_codes.map((c) => <Badge key={c} variant="outline" className="text-xs">{c}</Badge>)}
              </div>
            } />
            <Row label={isES ? 'Departamentos' : 'Departments'} value={`${departments.length}`} />
            <Row label={isES ? 'Posiciones' : 'Positions'} value={`${totalPositions}`} />
          </div>
        )}

        <DialogFooter className="flex items-center gap-2 pt-2 border-t">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="nc-back">
              <ChevronLeft className="h-4 w-4 mr-1" /> {isES ? 'Atrás' : 'Back'}
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {isES ? 'Cancelar' : 'Cancel'}
          </Button>
          {step < 4 ? (
            <Button onClick={() => setStep(step + 1)}
                    disabled={(step === 1 && !canStep1) || (step === 2 && !canStep2)}
                    data-testid="nc-next"
                    className="bg-gradient-primary">
              {isES ? 'Siguiente' : 'Next'} <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={!canSave || saving} data-testid="nc-save"
                    className="bg-gradient-primary">
              <CheckCircle2 className="h-4 w-4 mr-1" />
              {saving ? (isES ? 'Creando…' : 'Creating…') : (isES ? 'Crear cliente' : 'Create client')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
